import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { WidgetWrapper } from '../WidgetWrapper';
import { StateLabelBox } from '../StateLabelBox';
import { valveWrapper, svgBox, modeBox, modeTitle, modeValue } from './style';
import { AssetType } from 'src/types/assets';
import { STATE, MODE } from 'src/features/sites';
import { getStateOrModeData } from '../../utils';
import { ReadingsService } from 'src/services/readings/readings.service';
import { DataStructure } from '../../types';
import { useValveData } from 'src/features/sites/hooks';
import { ValveWidget } from 'src/assets/widgets/ValveWidget';

type Props = {
  widgetId: string;
  title: string;
  asset: AssetType | undefined;
  toggleAddOrRemoveHandler?: () => void;
  isEditMode?: boolean;
  testId?: string;
};

export const Valve = ({
  title,
  asset,
  toggleAddOrRemoveHandler,
  widgetId,
  testId,
  isEditMode,
}: Props) => {
  const [readings, setReadings] = useState<DataStructure>([]);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const { valveLiveData } = useValveData({ asset: asset! });

  const liveState = valveLiveData.state;
  const liveMode = valveLiveData.mode;

  useEffect(() => {
    const uniqueGatewayIds = Array.from(
      new Set(asset?.assignedTags?.map((item) => item.assignedGatewayId).filter(Boolean))
    );
    const requests = uniqueGatewayIds.map(async (gatewayId) => {
      if (!gatewayId) return Promise.resolve();

      const uniqueTagIds: string[] = Array.from(
        new Set(
          asset?.assignedTags
            ?.filter(
              (item) => item.assignedGatewayId === gatewayId && item.assignedTagId !== undefined
            )
            ?.map((item) => item.assignedTagId as string)
        )
      );

      try {
        const res = await ReadingsService.getLatestReadingsNewDataStorage({
          gatewayId: gatewayId,
          measurements: uniqueTagIds,
        });

        if (res !== null) {
          setReadings((prevState) => [...prevState, { gatewayId: gatewayId, values: res.data }]);
        }
      } catch (error) {
        console.error(error);
      }
    });

    Promise.all(requests).then(() => {
      setIsDataReady(true);
    });
  }, [asset]);

  const filteredAllAssetStates = useMemo(
    () =>
      asset?.assignedTags
        ?.filter((tag) => tag.tagName.toLowerCase() === STATE)
        .sort((a, b) => a.tagIndicatorPosition - b.tagIndicatorPosition),
    [asset]
  );

  const filteredAllAssetModes = useMemo(
    () =>
      asset?.assignedTags
        ?.filter((tag) => tag.tagName.toLowerCase() === MODE)
        .sort((a, b) => a.tagIndicatorPosition - b.tagIndicatorPosition),
    [asset]
  );

  const displayState = liveState
    ? liveState
    : getStateOrModeData({
        data: filteredAllAssetStates!!,
        latestReadings: readings,
      });
  const displayMode = liveMode
    ? liveMode
    : getStateOrModeData({
        data: filteredAllAssetModes!!,
        latestReadings: readings,
      });

  return (
    <WidgetWrapper
      title={title}
      isEditMode={isEditMode}
      widgetId={widgetId!}
      toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      testId={testId}
      isDataReady={isDataReady}
      isValveWidget={true}
      assetPriority={asset?.priority}
    >
      <Box sx={valveWrapper}>
        <Box sx={svgBox}>
          <ValveWidget />
        </Box>
        {displayState ? <StateLabelBox text={displayState} /> : <Typography>-</Typography>}
        <Box sx={modeBox}>
          <Typography sx={modeTitle}>Mode:</Typography>
          <Typography sx={modeValue}>{displayMode ?? '-'}</Typography>
        </Box>
      </Box>
    </WidgetWrapper>
  );
};
